/**
 * AVA-475 — THE report layout engine. One pagination, two renderers.
 *
 * The preview and the PDF are one document in two forms, and they were drifting:
 * the PDF flowed onto pages while the preview was a single scrolling column, so
 * "how many pages is this?" had no answer until you downloaded it. Two
 * independent paginations cannot be kept in step by discipline — the only fix is
 * to have one.
 *
 * WHAT THIS MODULE PRODUCES: absolute DRAW COMMANDS in millimetres, grouped by
 * page. `reportPdf.js` walks them into `doc.text` / `doc.addImage`;
 * `ReportPreview.vue` walks the same commands into absolutely-positioned
 * elements on scaled A4 sheets. Both renderers are dumb iterators — neither
 * measures, wraps, or decides a page break. That is what makes preview page
 * count == PDF page count a PROPERTY rather than a coincidence.
 *
 * FOUR THINGS HERE ARE LOAD-BEARING:
 *
 * 1. TEXT IS MEASURED WITH jsPDF, ALWAYS — including for the preview. Line
 *    breaks come from `splitTextToSize` against the real Helvetica metrics.
 *    Any renderer that wraps text itself has reintroduced the drift.
 *
 * 2. A POINT'S CORE IS UNSPLITTABLE, EVIDENCE IS NOT. A table split across a
 *    page break with its heading orphaned is the failure that actually reaches
 *    the reader, so a point block is reserved whole. Evidence images are
 *    continuation content: they flow across pages under an "(continued)"
 *    subhead, which is what stops a point with eight photographs from
 *    overflowing A4.
 *
 * 3. PAGE CHROME IS PART OF THE LAYOUT, NOT A POST-PASS. The repeated header on
 *    pages >= 2 and the "Page N of M" footer are emitted here, from the same
 *    constants that reserve the gutters they sit in. A post-pass that draws
 *    chrome after the fact can always land on top of content; this cannot.
 *
 * 4. EVERY UNSPLITTABLE UNIT MUST FIT ON AN EMPTY PAGE. An image taller than
 *    the page content area would be pushed to a fresh page forever, so images
 *    are capped against the TIGHTER of the two page geometries (pages >= 2,
 *    which lose height to the header gutter). `paginate` additionally always
 *    consumes the unit it is looking at, so no input can hang it.
 *
 * Sizes follow the AVA-356 template's type hierarchy — see TYPE.
 */
import jsPDF from 'jspdf';
import { describeMeasuredCount } from '@/features/Verification/utils/axisLock';
import { shortSupportRef } from '@/features/Verification/utils/supportRef';
import { formatEvidenceTime } from '@/features/Verification/utils/evidenceCapture';
import {
  REPORT_AXES,
  describePointOffsets,
  formatDeviation,
  formatPosition,
  formatTolerance,
} from '@/features/Verification/utils/reportFormat';

/** A4 portrait in millimetres, plus the reserved chrome gutters. */
export const PAGE = Object.freeze({
  width: 210,
  height: 297,
  margin: 14,
  /** Reserved at the top of pages >= 2 for the repeated support header. */
  headerGutter: 12,
  /** Reserved at the bottom of EVERY page for "Page N of M". */
  footerGutter: 10,
});

export const CONTENT_WIDTH = PAGE.width - PAGE.margin * 2;

/** Exported: the DOM renderer needs it to turn a command's pt size into px. */
export const PT_TO_MM = 0.3528;

/**
 * Leading as a multiple of the em. 1.7 reproduces the 6 mm line the previous
 * fixed-pitch layout used at 10 pt, so table density is unchanged while
 * headings now get leading proportional to their size.
 */
const LINE_FACTOR = 1.7;

/**
 * Baseline offset from the top of a line box. Helvetica's cap height is ~0.72
 * em; this is what lets the PDF (which positions by BASELINE) and the DOM
 * (which positions by TOP) place the same string in the same place.
 */
const ASCENT_FACTOR = 0.75;

/**
 * The template is a Word document at Word's default scale; dropped into A4 at
 * face value its 26 pt title eats a third of the page. TEMPLATE_SCALE preserves
 * the template's HIERARCHY while fitting our page — retune this ONE number, not
 * eight sizes. Needs a single live pass to settle.
 */
const TEMPLATE_SCALE = 0.68;

const scaled = (docxPt) => Math.round(docxPt * TEMPLATE_SCALE * 10) / 10;

/**
 * Type scale. The commented value is the source docx point size; sizes with no
 * docx counterpart (table body, captions) keep the sizes the PDF's column
 * geometry was tuned against and are NOT scaled.
 */
export const TYPE = Object.freeze({
  title: scaled(26),
  project: scaled(16),
  headerFact: scaled(14),
  status: scaled(18),
  sectionHead: scaled(18),
  resultsHead: scaled(16),
  decision: scaled(14),
  docFooter: scaled(11),
  body: 9,
  tableHead: 8,
  caption: 8,
  chrome: 8,
});

/**
 * Colours as [r, g, b] triples, NOT hex strings. jsPDF and CSS disagree about
 * which string forms they accept; a triple is unambiguous to both renderers.
 * Values track the feature's SCSS tokens.
 */
export const COLOR = Object.freeze({
  ink: Object.freeze([26, 26, 26]),
  soft: Object.freeze([110, 110, 110]),
  pass: Object.freeze([21, 108, 62]),
  fail: Object.freeze([183, 28, 28]),
  pending: Object.freeze([166, 124, 0]),
  rule: Object.freeze([190, 190, 190]),
});

/**
 * Column geometry in mm across the 210 mm page (usable 14–196).
 *
 * COLUMNS ARE POSITIONED, NOT PADDED. Helvetica is proportional, so
 * space-padding a string aligns nothing. `align: 'right'` columns name the
 * cell's RIGHT edge; left ones name its start.
 */
export const COL = Object.freeze({
  axis: PAGE.margin,
  design: 74,
  asBuilt: 106,
  deviation: 138,
  tolerance: 166,
  status: 172,
});

/** Left edge of the value column in the two-column facts table. */
const FACT_VALUE_X = 64;

/** Right edge of the content area — the limit every full-width line fits to. */
const RIGHT_EDGE = PAGE.width - PAGE.margin;

/** Clear space kept between two cells on the same row. */
const COLUMN_GUTTER = 3;

/** Column header of the cell to the right of the point-id cell. */
const DESIGN_HEADER = 'Design (m)';

/** Printed in place of any header fact nobody has supplied. */
const BLANK_FACT = '—';

const GAP_SM = 2;
const GAP_MD = 4;

/**
 * Breathing room between an image and the caption naming it.
 *
 * Without it the caption sits flush against the image edge and reads as part of
 * the picture rather than as a label for it — the two have no visual separation
 * at all, because the image has no border.
 */
const IMAGE_CAPTION_GAP = 3;

export function lineHeight(size) {
  return size * PT_TO_MM * LINE_FACTOR;
}

function ascent(size) {
  return size * PT_TO_MM * ASCENT_FACTOR;
}

/** Top of the content area. Pages >= 2 give up height to the repeated header. */
export function contentTop(pageIndex) {
  return PAGE.margin + (pageIndex === 0 ? 0 : PAGE.headerGutter);
}

/** Bottom of the content area. Every page reserves the footer gutter. */
export function contentBottom() {
  return PAGE.height - PAGE.margin - PAGE.footerGutter;
}

/**
 * A measuring function bound to its own private jsPDF document, carrying a
 * `.width()` companion.
 *
 * Private DELIBERATELY: `splitTextToSize` depends on the document's current
 * font, so sharing the drawing document would make measurement order-dependent
 * on drawing. Two instances cost nothing and cannot interleave.
 *
 * Callers injecting a measurer MUST get it from here — `fitText` needs
 * `.width`, and a bare splitter would silently stop truncating.
 *
 * @returns {(text: string, size: number, style: string, maxWidth: number) => string[]}
 */
export function createMeasurer() {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const useFont = (size, style) => doc.setFontSize(size).setFont('helvetica', style || 'normal');

  const measure = (text, size, style, maxWidth) => {
    useFont(size, style);
    return doc.splitTextToSize(String(text ?? ''), maxWidth);
  };
  measure.width = (text, size, style) => {
    useFont(size, style);
    return doc.getTextWidth(String(text ?? ''));
  };
  return measure;
}

/**
 * Trim `text` until it fits `maxWidth`, ending in an ellipsis when it had to.
 *
 * NOTHING ON THIS DOCUMENT MAY OVERRUN ITS COLUMN. jsPDF does not clip: a point
 * label wider than its cell simply prints straight through the Design column,
 * and the two strings overlap into something unreadable. Real data reaches
 * these cells — `reportId` falls back to a raw task GUID when the task has no
 * friendly id, and header overrides are free text up to 120 characters.
 *
 * A visible ellipsis is the honest outcome: the reader can tell something was
 * shortened, which silent overlap does not.
 *
 * THE SEARCH IS BINARY, AND THAT IS NOT AN OPTIMISATION — IT IS THE FIX FOR A
 * PAGE-FREEZING BUG. This trimmed one character per step and re-measured the
 * whole remaining string each time, which is O(n²): measured at 28 ms for 500
 * characters, 315 ms at 2 000, 4.8 s at 8 000, and past a minute at 32 000. A
 * long string reaching this function locked the main thread for minutes.
 * Prefix width is monotonic — a longer prefix is never narrower — so a binary
 * search is exact, not an approximation, and costs O(log n) measurements.
 *
 * The length clamp is the second half of the fix. `measure.width` is itself
 * O(length), so even ONE call on a pathological string is expensive; nothing
 * legitimate on this document exceeds ~190 characters (the full content width
 * at the smallest size), so anything past the clamp is malformed input, not
 * content.
 */
const FIT_TEXT_MAX_CHARS = 512;

function fitText(text, size, style, maxWidth, measure) {
  const value = String(text ?? '');
  if (!value || typeof measure.width !== 'function') return value;

  const clamped = value.length > FIT_TEXT_MAX_CHARS;
  const capped = clamped ? value.slice(0, FIT_TEXT_MAX_CHARS) : value;
  // A clamped string is ALWAYS marked as shortened, even in the impossible case
  // where it would fit — silently dropping content is the failure this whole
  // function exists to prevent.
  if (!clamped && measure.width(capped, size, style) <= maxWidth) return capped;

  // Largest prefix whose ellipsised form still fits.
  let low = 0;
  let high = capped.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (measure.width(`${capped.slice(0, mid)}…`, size, style) <= maxWidth) low = mid;
    else high = mid - 1;
  }
  return `${capped.slice(0, low).trimEnd()}…`;
}

// ---------------------------------------------------------------------------
// Row primitives. A ROW is the atom of flow: it has a height and knows how to
// emit its draw commands given the y at which it lands.
// ---------------------------------------------------------------------------

/** One baseline carrying N positioned cells. */
function textRow(items, size, options = {}) {
  return {
    kind: 'text',
    size,
    style: options.style || 'normal',
    color: options.color || COLOR.ink,
    height: options.height ?? lineHeight(size),
    items,
  };
}

/** Convenience for the common single-cell line. */
function textLine(text, size, options = {}) {
  return textRow(
    [{ text, x: options.x ?? PAGE.margin, align: options.align }],
    size,
    options,
  );
}

/**
 * `alt` is carried for the DOM renderer only — the PDF has no alt text. An
 * evidence image with no description is unreachable to a screen reader, and the
 * caption sitting under it is a separate command with no association to it.
 */
function imageRow(dataUrl, width, height, alt, options = {}) {
  return {
    kind: 'image',
    dataUrl,
    width,
    height,
    alt,
    // jsPDF needs to be told the encoding. Evidence is always JPEG because we
    // produce it; a reference drawing may arrive as a PNG.
    format: options.format || 'JPEG',
    x: options.x ?? PAGE.margin,
  };
}

function ruleRow(options = {}) {
  return {
    kind: 'rule',
    x: options.x ?? PAGE.margin,
    width: options.width ?? CONTENT_WIDTH,
    color: options.color || COLOR.rule,
    height: options.height ?? GAP_SM,
  };
}

function gapRow(height) {
  return { kind: 'gap', height };
}

/** Rows that must land together — an image and the caption that names it. */
function stackRow(rows) {
  return {
    kind: 'stack',
    rows,
    height: rows.reduce((sum, row) => sum + row.height, 0),
  };
}

/**
 * Rows placed as one unit; `continuation` heads each page after the first.
 * `breakBefore` starts the group on a fresh page.
 */
function group(rows, options = {}) {
  return {
    unsplittable: options.unsplittable !== false,
    continuation: options.continuation || null,
    breakBefore: !!options.breakBefore,
    rows,
  };
}

/** Absolute draw commands for one row landing with its top at `top`. */
function emitRow(row, top) {
  if (row.kind === 'gap') return [];

  if (row.kind === 'rule') {
    return [{
      kind: 'rule', x: row.x, y: top, width: row.width, color: row.color,
    }];
  }

  if (row.kind === 'image') {
    return [{
      kind: 'image',
      dataUrl: row.dataUrl,
      format: row.format,
      alt: row.alt,
      x: row.x,
      y: top,
      width: row.width,
      height: row.height,
    }];
  }

  if (row.kind === 'stack') {
    let y = top;
    return row.rows.reduce((commands, child) => {
      commands.push(...emitRow(child, y));
      y += child.height;
      return commands;
    }, []);
  }

  const baseline = top + ascent(row.size);
  return row.items
    .filter((item) => item.text !== null && item.text !== undefined && item.text !== '')
    .map((item) => ({
      kind: 'text',
      text: String(item.text),
      x: item.x,
      // BOTH are emitted: jsPDF positions text by its baseline, the DOM by the
      // top of the box. Deriving one from the other in each renderer is how the
      // two end up half a line apart.
      y: baseline,
      top,
      size: row.size,
      style: item.style || row.style,
      color: item.color || row.color,
      align: item.align || 'left',
    }));
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

/**
 * Flow groups onto pages.
 *
 * @param {Array} groups
 * @returns {{pages: Array<{index:number, commands:Array}>, pageCount:number}}
 */
export function paginate(groups) {
  const pages = [];
  let commands = [];
  let pageIndex = 0;
  let y = contentTop(0);

  const isEmpty = () => y <= contentTop(pageIndex);
  const place = (row) => {
    commands.push(...emitRow(row, y));
    y += row.height;
  };
  const breakPage = () => {
    pages.push({ index: pageIndex, commands });
    commands = [];
    pageIndex += 1;
    y = contentTop(pageIndex);
  };

  groups.forEach((block) => {
    // `!isEmpty()` matters: without it, a section asking for a fresh page while
    // already at the top of one emits a blank page before itself.
    if (block.breakBefore && !isEmpty()) breakPage();

    if (block.unsplittable) {
      const height = block.rows.reduce((sum, row) => sum + row.height, 0);
      // The `!isEmpty()` guard is what makes an oversized block terminate: on a
      // page that is already empty it is placed regardless and overflows, which
      // is a visible defect rather than an infinite loop. Images are capped so
      // this should be unreachable; the guard is for everything that is not an
      // image.
      if (y + height > contentBottom() && !isEmpty()) breakPage();
      block.rows.forEach(place);
      return;
    }

    let lastPage = null;
    block.rows.forEach((row) => {
      if (y + row.height > contentBottom() && !isEmpty()) breakPage();
      if (lastPage !== null && lastPage !== pageIndex && block.continuation) {
        place(block.continuation);
      }
      lastPage = pageIndex;
      place(row);
    });
  });

  pages.push({ index: pageIndex, commands });
  return { pages, pageCount: pages.length };
}

/**
 * Repeated header (pages >= 2) and "Page N of M" (every page).
 *
 * Emitted INTO the reserved gutters, so it cannot collide with content — see
 * note 3. Runs after pagination because M is not known before it.
 */
function addPageChrome(paginated, headerText) {
  const { pages, pageCount } = paginated;
  pages.forEach((page) => {
    if (page.index > 0 && headerText) {
      page.commands.unshift(
        ...emitRow(textLine(headerText, TYPE.chrome, { color: COLOR.soft }), PAGE.margin),
        ...emitRow(ruleRow(), PAGE.margin + PAGE.headerGutter - GAP_MD - GAP_SM),
      );
    }
    page.commands.push(
      ...emitRow(
        textLine(`Page ${page.index + 1} of ${pageCount}`, TYPE.chrome, {
          x: PAGE.width - PAGE.margin,
          align: 'right',
          color: COLOR.soft,
        }),
        PAGE.height - PAGE.margin - lineHeight(TYPE.chrome),
      ),
    );
  });
  return paginated;
}

// ---------------------------------------------------------------------------
// Document content
// ---------------------------------------------------------------------------

/**
 * Three-valued, NEVER a ternary. `pass` is true / false / null, and
 * `pass ? 'PASS' : 'FAIL'` prints a locked or unassessed axis as FAIL — a
 * fabricated finding on a conformance document.
 *
 * 'Locked' and 'Pending' are different claims: asserted from design and never
 * going to be measured, versus not measured yet.
 */
function axisVerdict(pass, isLocked) {
  if (isLocked) return { label: 'Locked', color: COLOR.pending };
  if (pass === true) return { label: 'PASS', color: COLOR.pass };
  if (pass === false) return { label: 'FAIL', color: COLOR.fail };
  return { label: 'Pending', color: COLOR.pending };
}

function overallColor(overall) {
  if (overall === 'WITHIN TOLERANCE') return COLOR.pass;
  if (overall === 'OUT OF TOLERANCE') return COLOR.fail;
  return COLOR.pending;
}

function fact(value) {
  const text = String(value ?? '').trim();
  return text || BLANK_FACT;
}

/**
 * Header facts, in the template's order. ONE FACT PER LINE.
 *
 * The previous inline pairing (`Support ID: X    Discipline: Y`) packed two
 * facts onto a line separated by run-on spaces, which is proportional-font
 * padding — it aligns nothing, so the second fact started at a different place
 * on every line and the block read as ragged prose. Stacked lines are what the
 * template does and they scan as a record.
 */
const HEADER_FACT_ORDER = Object.freeze([
  ['Building', 'building'],
  ['Room', 'room'],
  ['Support ID', 'supportNo'],
  ['Discipline', 'discipline'],
  ['Report ID', 'reportId'],
  ['Revision', 'revision'],
]);

/** Title, project, header facts, status. */
function headerGroups(support, date, overall, measure) {
  const facts = HEADER_FACT_ORDER.map(([label, key]) => [label, support[key]]);
  // Date is not a support field; it is appended so the block stays one list in
  // the template's order rather than a list plus an exception.
  facts.push(['Date', date]);

  const factLine = ([label, value]) => textLine(
    // Free-form data lands here — a discipline name, a task GUID standing in
    // for a report id — and each is a single unwrapped line.
    fitText(`${label}: ${fact(value)}`, TYPE.headerFact, 'bold', CONTENT_WIDTH, measure),
    TYPE.headerFact,
    { style: 'bold' },
  );

  return [
    group([
      textLine('AVA DIGITAL AS-BUILT VERIFICATION REPORT', TYPE.title, { style: 'bold' }),
      textLine('Hinkley Point C', TYPE.project, { color: COLOR.soft }),
      gapRow(GAP_SM),
      ...facts.map(factLine),
      gapRow(GAP_SM),
      textLine(`STATUS: ${overall}`, TYPE.status, { style: 'bold', color: overallColor(overall) }),
    ]),
  ];
}

/**
 * "Support Verification Details" — the template's 2-column metadata table.
 * Labels are the template's, EXACTLY.
 */
function supportFactGroups(support, measure) {
  const rows = [
    ['Discipline', support.discipline],
    ['Support No.', support.supportNo],
    ['Handbook No.', support.handbookNo],
    ['Model Version', support.modelVersion],
    ['Tolerance Spec', support.toleranceSpec],
  ].map(([label, value]) => textRow(
    [
      { text: label, x: PAGE.margin, color: COLOR.soft },
      {
        // Three of these five are surveyor-typed free text, capped at 120
        // characters — long enough to run off the page at this size.
        text: fitText(fact(value), TYPE.body, 'bold', RIGHT_EDGE - FACT_VALUE_X, measure),
        x: FACT_VALUE_X,
        style: 'bold',
      },
    ],
    TYPE.body,
  ));

  return [
    group([
      gapRow(GAP_MD),
      textLine('Support Verification Details', TYPE.sectionHead, { style: 'bold' }),
      ruleRow(),
      ...rows,
    ]),
  ];
}

/**
 * The lock flag as it rides in the table header cell: `Z locked`.
 *
 * SHORT ON PURPOSE. The full scope sentence (`X, Y check · Z locked`) is around
 * 40 mm at this size and the header cell has ~41 mm total, so inlining it would
 * push the point id out entirely. Which axes were CHECKED is the complement of
 * which were locked across a fixed three, so the short form loses nothing a
 * reader cannot recover — and the axis count sits under the table anyway.
 */
function lockQualifier(lockedKeys) {
  if (!lockedKeys.length) return '';
  return `${lockedKeys.map((axis) => axis.toUpperCase()).join(', ')} locked`;
}

/**
 * The table's first header cell: the point id, plus the lock flag when there
 * is one.
 *
 * THE QUALIFIER SURVIVES, THE LABEL GIVES WAY. When the combined string is too
 * wide it is the point ID that is shortened, never the lock flag: a truncated
 * id is still identifiable from the rows beneath it, whereas a truncated
 * "Z locked" leaves a locked-axis report reading as a completed three-axis
 * verification — precisely the misreading this qualifier exists to prevent.
 * The final pass is a floor, not the strategy: against a pathological qualifier
 * nothing can be kept whole, and not overrunning the Design column wins.
 */
function pointHeaderCell(label, qualifier, limit, measure) {
  if (!qualifier) return fitText(label, TYPE.tableHead, 'bold', limit, measure);

  const suffix = ` · ${qualifier}`;
  const room = limit - measure.width(suffix, TYPE.tableHead, 'bold');
  const head = room > 0 ? fitText(label, TYPE.tableHead, 'bold', room, measure) : label;
  return fitText(`${head}${suffix}`, TYPE.tableHead, 'bold', limit, measure);
}

/**
 * Width the Design column ACTUALLY occupies for one point.
 *
 * THE HEADER LITERAL IS NOT THE COLUMN. `Design (m)` measures 14.5 mm, but what
 * a reader perceives as the column's left edge is the widest VALUE under it, and
 * positions render at `POSITION_DECIMALS` (4). Three integer digits (589.1000,
 * 13.1 mm) is narrower than the header; six — an ordinary national-grid easting
 * — is 18.4 mm, or 19.4 mm signed. Budgeting the point title against the header
 * alone therefore licenses the title to grow past the left edge of the numeric
 * band directly beneath it.
 *
 * NOTHING OVERPRINTS — the title has its own baseline (its own `textRow`, above
 * the rule) so no two glyphs land on top of each other. That is exactly why this
 * is easy to dismiss and worth the comment: on a table of right-aligned numbers
 * the column's edge IS the structure, there is no rule or box marking it, and a
 * left-aligned title crossing it reads as a collision the reader cannot resolve.
 *
 * PER POINT, DELIBERATELY. Each `pointGroup` emits its own header row and rule,
 * so each table is self-contained and answers only to its own numbers. Hoisting
 * this to one document-wide measurement would truncate EVERY point title down to
 * the worst case appearing anywhere in the report.
 *
 * The header is measured bold at `tableHead`, the values normal at `body`. Those
 * are two different fonts; collapsing them into one measurement mis-sizes
 * whichever one loses.
 */
function designColumnWidth(point, measure) {
  const widths = [measure.width(DESIGN_HEADER, TYPE.tableHead, 'bold')];

  // An incomplete point prints "Pending" across the table instead of value rows,
  // so for it the header genuinely is the whole column.
  //
  // MEASURES `designSiteM`, THE SAME FIELD THE ROWS RENDER. Measuring
  // `designAdjusted` instead would size the column against viewer units — five
  // integer digits on a millimetre facility where two are printed — so the title
  // budget would be computed against a column that does not exist on the page.
  if (point.isComplete) {
    REPORT_AXES.forEach((axis) => widths.push(measure.width(
      formatPosition(point.designSiteM?.[axis.key]),
      TYPE.body,
      'normal',
    )));
  }

  return Math.max(...widths);
}

/**
 * One point. The CORE is unsplittable (note 2).
 *
 * The template puts the POINT ID in the table's first header cell rather than
 * on a line of its own, so that is where it goes — and the LOCK FLAG goes with
 * it. RULE 1 on the document: a check with axes locked is honestly a check of
 * the free axes, and the documented conformance risk is a report that LOOKS
 * like a completed three-axis verification. That warning has to be legible
 * BEFORE the numbers it qualifies, which after the template's header-cell shape
 * removed the old title line leaves exactly one place for it. The measured-axis
 * count, the offsets and the comment follow the table: they elaborate on a
 * caveat the reader has already been given.
 */
function pointGroup(point, ref, measure) {
  const locked = point.lockedAxes || [];
  // The point id opens the table's header row and sits at the head of the
  // right-aligned Design column, and a surveyor can rename a point to anything
  // up to 48 characters. Unfitted, a long name runs straight into that column —
  // jsPDF does not clip.
  //
  // BUDGET AGAINST THE COLUMN, NOT AGAINST `DESIGN_HEADER`. Measuring the header
  // literal here was the original defect: it makes the title's licence
  // independent of the numbers, so every extra digit in a coordinate eats the
  // gutter directly and the title creeps into the value band. See
  // `designColumnWidth`. For today's three-digit site data the header is the
  // wider of the two and this computes the same limit it always did.
  const titleLimit = COL.design
    - designColumnWidth(point, measure)
    - COL.axis
    - COLUMN_GUTTER;
  const title = pointHeaderCell(
    `${ref}.${point.label}`,
    lockQualifier(locked),
    titleLimit,
    measure,
  );
  const rows = [gapRow(GAP_MD)];

  rows.push(textRow(
    [
      { text: title, x: COL.axis },
      { text: DESIGN_HEADER, x: COL.design, align: 'right' },
      { text: 'As-Built (m)', x: COL.asBuilt, align: 'right' },
      { text: 'Deviation (mm)', x: COL.deviation, align: 'right' },
      { text: 'Tolerance (mm)', x: COL.tolerance, align: 'right' },
      { text: 'Status', x: COL.status },
    ],
    TYPE.tableHead,
    { style: 'bold' },
  ));
  rows.push(ruleRow({ height: GAP_SM }));

  if (point.isComplete) {
    REPORT_AXES.forEach((axis) => {
      const isLocked = locked.includes(axis.key);
      const verdict = axisVerdict(point[axis.pass], isLocked);
      rows.push(textRow(
        [
          { text: axis.axis, x: COL.axis },
          // SITE metres, attached to the point by GET_REPORT_MODEL — not the
          // viewer-unit `designAdjusted` the deviation is derived from. The
          // column heading says "(m)" and this is the only field on the point
          // for which that is true on every facility.
          { text: formatPosition(point.designSiteM?.[axis.key]), x: COL.design, align: 'right' },
          { text: formatPosition(point.asBuiltSiteM?.[axis.key]), x: COL.asBuilt, align: 'right' },
          {
            // ASCII sign: jsPDF's Helvetica is WinAnsi (cp1252) and U+2212 is
            // not in it, so a -5 mm deviation would print as 5 mm on a
            // conformance document. A locked axis's deviation is zero BY
            // CONSTRUCTION — printing "+0.0" invites it to be read as a
            // measurement that came out perfect.
            text: isLocked ? '-' : formatDeviation(point.deltas?.[axis.delta], true),
            x: COL.deviation,
            align: 'right',
          },
          { text: formatTolerance(point.tolerance?.[axis.key]), x: COL.tolerance, align: 'right' },
          { text: verdict.label, x: COL.status, color: verdict.color, style: 'bold' },
        ],
        TYPE.body,
      ));
    });
  } else {
    rows.push(textLine('Pending — as-built point not captured', TYPE.body, { color: COLOR.pending }));
  }

  const offsetLine = describePointOffsets(point);
  if (offsetLine) {
    rows.push(textLine(
      fitText(offsetLine, TYPE.caption, 'normal', CONTENT_WIDTH, measure),
      TYPE.caption,
      { color: COLOR.soft },
    ));
  }

  // The count that backs up the header cell's flag. It stays down here because
  // it does not fit up there and because it is detail, not warning — the
  // warning is already above the numbers.
  if (locked.length) {
    rows.push(textLine(
      fitText(
        describeMeasuredCount(point.lockedValues || {}),
        TYPE.caption,
        'bold',
        CONTENT_WIDTH,
        measure,
      ),
      TYPE.caption,
      { style: 'bold', color: COLOR.pending },
    ));
  }

  const comment = String(point.comment || '').trim();
  if (comment) {
    measure(`Point comment: ${comment}`, TYPE.body, 'normal', CONTENT_WIDTH)
      .forEach((commentLine) => rows.push(textLine(commentLine, TYPE.body, { color: COLOR.soft })));
  }

  return group(rows);
}

function resultGroups(model, ref, measure) {
  const groups = [group([
    gapRow(GAP_MD),
    textLine('Verification Results', TYPE.resultsHead, { style: 'bold' }),
    ruleRow(),
  ])];

  if (!model.points.length) {
    groups.push(group([textLine('No points added.', TYPE.body, { color: COLOR.soft })]));
    return groups;
  }

  model.points.forEach((point) => groups.push(pointGroup(point, ref, measure)));
  return groups;
}

function notesGroups(notes, measure) {
  const text = String(notes || '').trim();
  if (!text) return [];

  const rows = measure(text, TYPE.body, 'normal', CONTENT_WIDTH)
    .map((noteLine) => textLine(noteLine, TYPE.body));

  return [
    group([
      gapRow(GAP_MD),
      textLine('Notes', TYPE.resultsHead, { style: 'bold' }),
      ruleRow(),
    ]),
    // Prose may paginate freely — only tables must stay whole.
    group(rows, {
      unsplittable: false,
      continuation: textLine('Notes (continued)', TYPE.caption, { color: COLOR.soft }),
    }),
  ];
}

/**
 * Largest image height that is guaranteed to fit, caption included.
 *
 * Bounded against pages >= 2, which are SHORTER than page 1 by the header
 * gutter. Capping against page 1 would let an image that fits there be pushed
 * to page 2, not fit, be pushed again… (note 4).
 */
function maxImageHeight() {
  const tightest = contentBottom() - contentTop(1);
  // Everything stacked with the image has to come out of the same budget: the
  // continuation subhead, the caption, the gap above it, and the trailing gap.
  return tightest
    - lineHeight(TYPE.caption)
    - lineHeight(TYPE.caption)
    - IMAGE_CAPTION_GAP
    - GAP_MD;
}

/**
 * Evidence images, in one section AFTER the final decision — the placement the
 * template uses, at near content width. Storage stays per point; this is a
 * rendering decision, and the caption is what ties each image back to its point.
 */
function evidenceGroups(points, measure) {
  const shots = points.flatMap((point) => (point.evidence || []).map((shot) => ({
    ...shot,
    pointLabel: point.label,
  })));
  if (!shots.length) return [];

  const cap = maxImageHeight();
  const rows = shots.map((shot) => {
    const aspect = shot.width > 0 && shot.height > 0 ? shot.height / shot.width : 0.5625;
    const width = Math.min(CONTENT_WIDTH, cap / aspect);
    const height = width * aspect;
    const caption = [shot.pointLabel, shot.viewLabel, formatEvidenceTime(shot.capturedAt)]
      .filter(Boolean)
      .join(' · ');
    return stackRow([
      imageRow(shot.dataUrl, width, height, `Evidence for ${caption}`),
      gapRow(IMAGE_CAPTION_GAP),
      textLine(
        // Carries the point's label, which the surveyor can rename freely.
        fitText(caption, TYPE.caption, 'normal', CONTENT_WIDTH, measure),
        TYPE.caption,
        { color: COLOR.soft },
      ),
      gapRow(GAP_MD),
    ]);
  });

  return [
    // Evidence opens a fresh page. The images are near content-width, so
    // starting them under the tail of the decision block left one stranded
    // image on the preceding page and pushed the rest over anyway.
    group([
      textLine('Evidence', TYPE.resultsHead, { style: 'bold' }),
      ruleRow(),
    ], { breakBefore: true }),
    group(rows, {
      unsplittable: false,
      continuation: textLine('Evidence (continued)', TYPE.caption, { color: COLOR.soft }),
    }),
  ];
}

function decisionGroups(overall) {
  return [group([
    gapRow(GAP_MD),
    ruleRow({ color: COLOR.ink }),
    textLine(`FINAL DECISION: ${overall}`, TYPE.decision, {
      style: 'bold',
      color: overallColor(overall),
    }),
  ])];
}

/**
 * The reference drawing, as its own full page at the end of the report.
 *
 * Sized to the page rather than to the image: a drawing is read, not glanced
 * at, so it takes whichever of width or height binds first and keeps its
 * aspect. Bounded by the page-2 content height like every other image, so a
 * tall drawing cannot reintroduce the overflow the evidence cap fixed.
 *
 * Absent for every PDF and Office reference — see utils/referenceDrawing.js for
 * why that is a limit rather than an omission.
 */
function drawingGroups(drawing, measure) {
  if (!drawing?.dataUrl) return [];

  const heading = lineHeight(TYPE.resultsHead) + GAP_SM;
  // Same caption spacing as the evidence images, so the two pages read as one
  // document rather than as two conventions.
  const caption = lineHeight(TYPE.caption) + IMAGE_CAPTION_GAP;
  const available = contentBottom() - contentTop(1) - heading - caption - GAP_MD;
  const aspect = drawing.width > 0 && drawing.height > 0
    ? drawing.height / drawing.width
    : 1 / Math.SQRT2;
  const width = Math.min(CONTENT_WIDTH, available / aspect);

  return [
    group([
      textLine('Reference Drawing', TYPE.resultsHead, { style: 'bold' }),
      ruleRow(),
      imageRow(
        drawing.dataUrl,
        width,
        width * aspect,
        `Reference drawing: ${drawing.name}`,
        { format: drawing.format },
      ),
      gapRow(IMAGE_CAPTION_GAP),
      textLine(
        fitText(drawing.name || '', TYPE.caption, 'normal', CONTENT_WIDTH, measure),
        TYPE.caption,
        { color: COLOR.soft },
      ),
    ], { breakBefore: true }),
  ];
}

function closingGroups(ref, measure) {
  return [group([
    gapRow(GAP_MD),
    textLine(fitText(ref, TYPE.caption, 'normal', CONTENT_WIDTH, measure), TYPE.caption, {
      color: COLOR.soft,
    }),
    textLine('Generated by AVA Core | Digital Twin Platform', TYPE.docFooter, {
      color: COLOR.soft,
    }),
  ])];
}

/**
 * Build every flow group for a report model, in template order:
 * title → project → header facts → STATUS → Support Verification Details →
 * Verification Results → FINAL DECISION → Evidence → support ref → footer.
 */
export function buildReportGroups(model, measure) {
  const support = model?.support || {};
  const points = model?.points || [];
  const overall = model?.overall || 'INCOMPLETE';
  const ref = shortSupportRef(support.supportNo, support.building);

  return [
    ...headerGroups(support, model?.date, overall, measure),
    ...supportFactGroups(support, measure),
    ...resultGroups({ ...model, points }, ref, measure),
    ...notesGroups(model?.notes, measure),
    ...decisionGroups(overall),
    ...evidenceGroups(points, measure),
    ...drawingGroups(model?.drawing, measure),
    ...closingGroups(ref, measure),
  ];
}

/**
 * The single entry point both renderers use.
 *
 * @param {object} model `VERIFICATION/GET_REPORT_MODEL`.
 * @param {{measure?: Function}} [options] Inject a measurer to share one jsPDF
 *   instance across repeated layouts; omitted, a private one is created.
 * @returns {{pages: Array<{index:number, commands:Array}>, pageCount:number}}
 */
export function layoutReport(model, options = {}) {
  const measure = options.measure || createMeasurer();
  const support = model?.support || {};
  const headerText = [support.supportNo, support.reportId].filter(Boolean).join(' · ');
  return addPageChrome(paginate(buildReportGroups(model, measure)), headerText);
}

export default layoutReport;
